import type { ButtonHTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';

export type IconButtonSize = 'sm' | 'md' | 'lg';

export type IconButtonProps = {
  size?: IconButtonSize;
  /** Show an unread indicator. A number renders the compact Figma count badge. */
  badge?: boolean | number;
  children: ReactNode;
} & ButtonHTMLAttributes<HTMLButtonElement>;

const base = clsx(
  'relative inline-flex items-center justify-center',
  'rounded-sm bg-transparent text-text-secondary cursor-pointer',
  'transition-[background-color,color] duration-fast ease-standard',
  'enabled:hover:bg-ink-100 enabled:hover:text-text-primary',
  'disabled:opacity-40 disabled:cursor-not-allowed',
);

const sizes: Record<IconButtonSize, string> = {
  sm: 'w-7 h-7',
  md: 'w-8 h-8',
  lg: 'w-10 h-10',
};

export function IconButton({
  size = 'md',
  badge,
  className,
  type = 'button',
  children,
  ...rest
}: IconButtonProps) {
  const numericBadge = typeof badge === 'number' ? badge : undefined;

  return (
    <button type={type} data-size={size} className={clsx(base, sizes[size], className)} {...rest}>
      {children}
      {numericBadge !== undefined ? (
        <span
          aria-label={`${numericBadge} unread notifications`}
          data-badge
          className="absolute left-6 top-[9px] inline-flex h-[14px] min-w-[18px] items-center justify-center rounded-pill bg-[#ef4444] px-[4px] text-[10px] font-bold leading-none text-white"
        >
          {numericBadge}
        </span>
      ) : badge ? (
        <span
          aria-label="Has unread notifications"
          data-badge
          className="absolute right-1 top-1 h-2 w-2 rounded-full border-2 border-surface-card bg-[#ef4444]"
        />
      ) : null}
    </button>
  );
}
